import type { Element as DiagramElement } from "diagram-js/lib/model/Types";
import type { Shape } from "bpmn-js/lib/model/Types";
import AutoPlace from "diagram-js/lib/features/auto-place/AutoPlace";
import Create from "diagram-js/lib/features/create/Create";
import ContextPad, {
    ContextPadTarget,
} from "diagram-js/lib/features/context-pad/ContextPad";
import {
    ContextPadEntries,
    ContextPadConfig,
    Injector,
    AppendPreview,
} from "bpmn-js/lib/features/context-pad/ContextPadProvider";
import ContextPadProvider from "diagram-js/lib/features/context-pad/ContextPadProvider";
import ElementFactory from "bpmn-js/lib/features/modeling/ElementFactory";
import Translate from "diagram-js/lib/i18n/translate/translate";
import { assign } from "min-dash";
import { is } from "bpmn-js/lib/util/ModelUtil";

export default class StekzContextPadProvider implements ContextPadProvider {
    static $inject = [
        "contextPad",
        "config",
        "create",
        "elementFactory",
        "appendPreview",
        "translate",
        "injector",
    ];

    static hiddenElements: string[] = [];
    private autoPlace?: AutoPlace;

    constructor(
        contextPad: ContextPad,
        config: ContextPadConfig,
        private create: Create,
        private elementFactory: ElementFactory,
        private appendPreview: AppendPreview,
        private translate: typeof Translate,
        injector: Injector
    ) {
        const config_ = config || {};

        if (config_.autoPlace !== false) {
            this.autoPlace = injector.get("autoPlace", false) || undefined;
        }

        // Register as palette provider
        contextPad.registerProvider(this);
    }

    getContextPadEntries(target: DiagramElement): ContextPadEntries {
        /**
         * See bpmn-js/lib/features/context-pad/ContextPadProvider for appendAction.
         */
        const appendCallActivity = (
            type: string,
            className: string,
            title: string
        ) => {
            /**
             * Create at start.
             */
            const appendStart = (event: Event, element: ContextPadTarget) => {
                const shape = this.elementFactory.createShape(
                    assign({ type: type })
                );

                this.create.start(event, shape, { source: element });
            };

            /**
             * Apend to a given element.
             */
            const append = this.autoPlace
                ? (_: Event, element: ContextPadTarget) => {
                      const shape = this.elementFactory.createShape(
                          assign({ type: type })
                      );
                      this.autoPlace!.append(element as Shape, shape);
                  }
                : appendStart;

            /**
             * Preview the append.
             */
            const previewAppend = this.autoPlace
                ? (_: Event, element: ContextPadTarget) => {
                      this.appendPreview.create(element as Shape, type, {});
                      // Return the function on mouse leave
                      return () => this.appendPreview.cleanUp();
                  }
                : (_: Event, __: ContextPadTarget) => {};

            return {
                group: "model",
                className: className,
                title: title,
                action: {
                    dragstart: appendStart,
                    click: append,
                    hover: previewAppend,
                },
            };
        };

        if (!is(target, "bpmn:EndEvent")) {
            return {
                "append.append-call-activity": appendCallActivity(
                    "bpmn:CallActivity",
                    "bpmn-icon-call-activity",
                    this.translate("Append call activity")
                ),
            };
        } else {
            return {};
        }
    }
}
